import { styled } from '@linaria/react'
import React, { type FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import { formatTon } from '../utils/formatTon'
import { useTaskTemplates } from '../hooks/useBonusData'
import { useAppContext } from '../contexts/App.context'
import { Loader } from '../ui/Loader'
import TonIcon from '../svgr/TonIcon'

/**
 * Preview component showing available bonus tasks for unauthenticated users
 * to motivate them to connect their wallets
 */
export const BonusTasksPreview: FC = () => {
  const { language } = useAppContext()
  const { data: taskTemplates, isLoading, error } = useTaskTemplates()

  if (isLoading) {
    return (
      <Container>
        <Typography
          fontSize={12}
          color={colors.textSecondary}
          style={{ textAlign: 'center' }}
        >
          <FormattedMessage
            id="bonus.preview.loading"
            defaultMessage="Загружаем доступные награды..."
          />
        </Typography>
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      </Container>
    )
  }

  if (error || !taskTemplates?.tasks?.length) {
    return (
      <Container>
        <Typography
          fontSize={12}
          color={colors.textSecondary}
          style={{ textAlign: 'center' }}
        >
          <FormattedMessage
            id="bonus.preview.unavailable"
            defaultMessage="Информация о наградах временно недоступна"
          />
        </Typography>
      </Container>
    )
  }

  return (
    <Container>
      <HeaderText fontSize={14} fontWeight={600}>
        <FormattedMessage
          id="bonus.preview.header"
          defaultMessage="Доступные награды за задания:"
        />
      </HeaderText>

      <TasksList>
        {taskTemplates.tasks.map(task => (
          <TaskCard key={task.taskKey}>
            <TaskInfo>
              <TaskTitle fontSize={13} fontWeight={600}>
                {language === 'en' ? task.titleEn : task.titleRu}
              </TaskTitle>
              <TaskDescription fontSize={12}>
                {language === 'en' ? task.descriptionEn : task.descriptionRu}
              </TaskDescription>
            </TaskInfo>

            <RewardAmount fontSize={13} fontWeight={700}>
              {formatTon(task.reward, 2)} <TonIcon />
            </RewardAmount>
          </TaskCard>
        ))}
      </TasksList>

      <MotivationText fontSize={12}>
        <FormattedMessage
          id="bonus.preview.motivation"
          defaultMessage="Подключите кошелек, чтобы начать зарабатывать!"
        />
      </MotivationText>
    </Container>
  )
}

const Container = styled.div`
  display: grid;
  gap: 16px;
  padding: 16px;
  background: ${colors.surfaceSecondary};
  border-radius: 16px;
  margin: 16px 0;
`

const HeaderText = styled(Typography)`
  color: ${colors.textPrimary};
  text-align: center;
`

const TasksList = styled.div`
  display: grid;
  gap: 8px;
`

const TaskCard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 12px;
  background: ${colors.surfacePrimary};
  border-radius: 12px;
  align-items: center;
`

const TaskInfo = styled.div`
  display: grid;
  gap: 4px;
`

const TaskTitle = styled(Typography)`
  color: ${colors.textPrimary};
`

const TaskDescription = styled(Typography)`
  color: ${colors.textSecondary};
  line-height: 1.3;
`

const RewardAmount = styled(Typography)`
  color: ${colors.primary};
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
`

const MotivationText = styled(Typography)`
  color: ${colors.textSecondary};
  text-align: center;
  padding: 8px 0;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`
